/**
 * TareasGlobal — Sprint B IMPL-046 (2026-05-22).
 *
 * Vista global de tareas del comercial: filtros + tabla con acción "Abrir actividad cliente".
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, Loader2, CheckSquare, Square, ExternalLink, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const ORIGENES = ["acta", "oferta", "manual"];

const ORIGEN_COLOR = {
  acta: "bg-indigo-100 text-indigo-700",
  oferta: "bg-blue-100 text-blue-700",
  manual: "bg-slate-100 text-slate-600",
};

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

const RANGOS = [
  { v: "hoy", label: "Hoy", fn: () => { const h = new Date(); return { d: ymd(h), h: ymd(h) }; } },
  { v: "semana", label: "Esta semana", fn: () => {
      const n = new Date();
      const dia = n.getDay() || 7;
      const inicio = new Date(n); inicio.setDate(n.getDate() - dia + 1);
      const fin = new Date(inicio); fin.setDate(inicio.getDate() + 6);
      return { d: ymd(inicio), h: ymd(fin) };
    } },
  { v: "mes", label: "Este mes", fn: () => {
      const n = new Date();
      const inicio = new Date(n.getFullYear(), n.getMonth(), 1);
      const fin = new Date(n.getFullYear(), n.getMonth() + 1, 0);
      return { d: ymd(inicio), h: ymd(fin) };
    } },
];

export default function TareasGlobal({ onBack, onOpenCliente }) {
  const [tareas, setTareas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pendiente");
  const [search, setSearch] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroOrigen, setFiltroOrigen] = useState("");
  const [rango, setRango] = useState("");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, filtroCliente, filtroOrigen, rango]);

  useEffect(() => {
    authAxios.get(`${API}/clientes?limit=500`).then(r => setClientes(r.data || [])).catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab !== "todas") params.append("estado", tab);
      if (filtroCliente) params.append("cliente_id", filtroCliente);
      if (filtroOrigen) params.append("origen", filtroOrigen);
      if (rango) {
        const r = RANGOS.find(x => x.v === rango)?.fn();
        if (r) {
          params.append("fecha_desde", r.d);
          params.append("fecha_hasta", r.h);
        }
      }
      params.append("limit", "200");
      const r = await authAxios.get(`${API}/tareas?${params}`);
      setTareas(r.data || []);
    } catch {
      toast.error("Error cargando tareas");
    }
    setLoading(false);
  }

  const tareasFiltradas = useMemo(() => {
    if (!search) return tareas;
    const s = search.toLowerCase();
    return tareas.filter(t => (t.texto || "").toLowerCase().includes(s));
  }, [tareas, search]);

  const clientesMap = useMemo(() => {
    const m = {};
    clientes.forEach(c => { m[c.id] = c; });
    return m;
  }, [clientes]);

  const toggleEstado = async (t) => {
    const nuevo = t.estado === "completada" ? "pendiente" : "completada";
    try {
      await authAxios.put(`${API}/tareas/${t.id}`, { estado: nuevo });
      toast.success(nuevo === "completada" ? "Completada" : "Reabierta");
      load();
    } catch {
      toast.error("Error actualizando tarea");
    }
  };

  const abrirCliente = (t) => {
    const c = clientesMap[t.cliente_id];
    if (c && onOpenCliente) onOpenCliente(c);
    else toast.error("Cliente no encontrado");
  };

  const limpiar = () => {
    setSearch(""); setFiltroCliente(""); setFiltroOrigen(""); setRango("");
  };

  const pendientesCount = tareas.filter(t => t.estado === "pendiente").length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0a6640] px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} data-testid="tareas-back-btn" className="text-white/60 hover:text-white flex items-center gap-1 text-sm flex-shrink-0">
            <ArrowLeft className="w-4 h-4" /> Volver
          </button>
          <span className="text-white/30">·</span>
          <span className="text-sm font-semibold text-white truncate">Mis tareas · Desarrollo Comercial</span>
        </div>
      </header>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 flex gap-1">
          <Tab id="pendiente" current={tab} onClick={setTab} testid="tareas-tab-pendiente">
            Pendientes <span className="ml-1 text-xs bg-slate-100 px-1.5 py-0.5 rounded-full">{tab === "pendiente" ? tareasFiltradas.length : pendientesCount}</span>
          </Tab>
          <Tab id="completada" current={tab} onClick={setTab} testid="tareas-tab-completada">Completadas</Tab>
          <Tab id="todas" current={tab} onClick={setTab} testid="tareas-tab-todas">Todas</Tab>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 py-4">
        <div className="bg-white border border-slate-200 rounded-xl mb-4">
          <div className="flex gap-2 items-center p-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar tarea..."
                data-testid="tareas-search"
                className="w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <select
              value={filtroCliente}
              onChange={(e) => setFiltroCliente(e.target.value)}
              data-testid="tareas-filtro-cliente"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">Todos los clientes</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
            </select>
            <select
              value={filtroOrigen}
              onChange={(e) => setFiltroOrigen(e.target.value)}
              data-testid="tareas-filtro-origen"
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white"
            >
              <option value="">Todos los orígenes</option>
              {ORIGENES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            {RANGOS.map(r => (
              <button
                key={r.v}
                onClick={() => setRango(rango === r.v ? "" : r.v)}
                data-testid={`tareas-rango-${r.v}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  rango === r.v ? "bg-[#0a6640] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r.label}
              </button>
            ))}
            {(search || filtroCliente || filtroOrigen || rango) && (
              <button onClick={limpiar} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
                <X className="w-3 h-3" /> Limpiar
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : tareasFiltradas.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            <CheckSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Sin tareas que coincidan</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="w-10"></th>
                  <th className="text-left px-3 py-2.5 font-semibold">Tarea</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Cliente</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Origen</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Fecha límite</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Estado</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tareasFiltradas.map((t) => (
                  <FilaTarea
                    key={t.id}
                    tarea={t}
                    cliente={clientesMap[t.cliente_id]}
                    onToggle={() => toggleEstado(t)}
                    onAbrir={() => abrirCliente(t)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Tab({ id, current, onClick, testid, children }) {
  const active = current === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={testid}
      className={`px-4 py-2.5 text-sm border-b-2 transition whitespace-nowrap ${
        active ? "border-[#0a6640] text-[#0a6640] font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function FilaTarea({ tarea, cliente, onToggle, onAbrir }) {
  const completada = tarea.estado === "completada";
  const fecha = tarea.fecha_limite ? new Date(tarea.fecha_limite) : null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const enSemana = fecha ? (fecha - hoy) / 86400000 <= 7 : false;
  const pasada = fecha && fecha < hoy && !completada;

  const fechaColor = pasada ? "text-rose-600 font-semibold" : enSemana ? "text-amber-700" : "text-slate-600";
  const fechaTxt = fecha ? fecha.toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "—";

  return (
    <tr className="hover:bg-slate-50 transition" data-testid={`tarea-row-${tarea.id}`}>
      <td className="px-3 py-2.5 text-center">
        <button onClick={onToggle} data-testid={`tarea-toggle-${tarea.id}`} className="text-slate-400 hover:text-emerald-600">
          {completada ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4" />}
        </button>
      </td>
      <td className={`px-3 py-2.5 ${completada ? "line-through text-slate-400" : "text-slate-800"}`}>
        {tarea.texto}
      </td>
      <td className="px-3 py-2.5 text-slate-600 truncate max-w-[200px]">
        {cliente?.empresa || <span className="text-slate-400 italic">cliente?</span>}
      </td>
      <td className="px-3 py-2.5">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ORIGEN_COLOR[tarea.origen] || ORIGEN_COLOR.manual}`}>
          {tarea.origen}
        </span>
      </td>
      <td className={`px-3 py-2.5 ${fechaColor}`}>
        {fechaTxt}
      </td>
      <td className="px-3 py-2.5">
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
          completada ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
        }`}>
          {completada ? "completada" : "pendiente"}
        </span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <button
          onClick={onAbrir}
          disabled={!cliente}
          data-testid={`tarea-abrir-${tarea.id}`}
          className="text-xs text-emerald-700 hover:text-emerald-900 disabled:opacity-30 flex items-center gap-1"
        >
          Abrir <ExternalLink className="w-3 h-3" />
        </button>
      </td>
    </tr>
  );
}
